"use client";

import type { Espece } from "@repo/types";
import type { EspeceSelectInput } from "@repo/types";
import * as React from "react";
import ReactSelect, { components, type SingleValue } from "react-select";
import { useEspeces } from "@repo/ui/hooks/api/especes/index";
import { useDebounce } from "@repo/ui/hooks/use-debounce";
import {
  type EspeceSelectOption,
  especesToSelectOptions,
} from "../utils/espece-select-utils";
import { PlusCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { AddEspeceQuickForm } from "./add-espece-quick-form";

// ---------------------------------------------------------------------------
// Custom MenuList — adds "Ajouter une essence" button at the bottom.
// Defined at module level for a stable reference (avoids React remounting).
// The click handler is injected via selectProps.onAddEspece (react-select pattern).
// ---------------------------------------------------------------------------
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const EspeceMenuList = (props: any) => {
  const { children, selectProps } = props;
  return (
    <components.MenuList {...props}>
      {children}
      <div className="sticky bottom-0 border-t border-border bg-background px-2 py-1.5">
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            selectProps?.onAddEspece?.();
          }}
          className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium text-primary transition-colors hover:bg-primary/5"
        >
          <PlusCircle className="h-4 w-4 shrink-0" />
          Ajouter une essence
        </button>
      </div>
    </components.MenuList>
  );
};

const ESPECE_SELECT_COMPONENTS = { MenuList: EspeceMenuList };

export type { EspeceSelectOption };

export interface EspeceSelectProps {
  value?: string;
  onChange: (value: string | null) => void;
  onEspeceSelect?: (espece: Espece | null) => void;
  onBlur?: () => void;

  /** Si fourni, utilise ces options (pas d'appel API) */
  options?: EspeceSelectOption[] | { label: string; options: EspeceSelectOption[] }[];
  isLoading?: boolean;
  restrictToIds?: number[];

  searchMode?: "server" | "client";
  fetchFilters?: Partial<EspeceSelectInput>;

  groupBy?: "categorie" | "genre" | false;
  placeholder?: string;
  emptyMessage?: string;

  disabled?: boolean;
  clearable?: boolean;
  searchable?: boolean;

  variant?: "default" | "form";
  error?: boolean;
  className?: string;

  menuPortalTarget?: HTMLElement | null;
}

const DEFAULT_EMPTY_MESSAGE = "Aucune essence trouvée";

export function EspeceSelect({
  value,
  onChange,
  onEspeceSelect,
  onBlur,
  options: optionsProp,
  isLoading: isLoadingProp,
  restrictToIds,
  searchMode = "server",
  fetchFilters = {},
  groupBy = false,
  placeholder = "Rechercher une essence...",
  emptyMessage = DEFAULT_EMPTY_MESSAGE,
  disabled = false,
  clearable = true,
  searchable = true,
  variant = "default",
  error = false,
  className = "react-select-container",
  menuPortalTarget,
}: EspeceSelectProps) {
  const [searchTerm, setSearchTerm]       = React.useState("");
  const [inputValue, setInputValue]       = React.useState("");
  const [isAddDialogOpen, setIsAddDialogOpen] = React.useState(false);

  const handleOpenAddDialog = React.useCallback(() => setIsAddDialogOpen(true), []);
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const useInternalFetch = !optionsProp;

  const { data: especeData, isLoading: isLoadingInternal } = useEspeces({
    search: searchMode === "server" ? debouncedSearchTerm : undefined,
    limit: 2000,
    page: 1,
    sortBy: "alphanum",
    includeDeleted: false,
    ...fetchFilters,
  });

  const internalOptions = React.useMemo(() => {
    if (!useInternalFetch || !especeData?.data) return [];
    return especesToSelectOptions(especeData.data, {
      groupBy,
      restrictToIds,
      includeData: !!onEspeceSelect,
    }) as EspeceSelectOption[] | { label: string; options: EspeceSelectOption[] }[];
  }, [useInternalFetch, especeData?.data, groupBy, restrictToIds, onEspeceSelect]);

  const allOptions = React.useMemo(() => {
    if (optionsProp) return optionsProp;

    if (searchMode === "server") {
      return internalOptions;
    }

    const filterTerm = inputValue.trim().toLowerCase();
    if (!filterTerm) return internalOptions;

    const flatOpts = Array.isArray(internalOptions) && internalOptions.length > 0
      ? "options" in (internalOptions[0] ?? {})
        ? (internalOptions as { label: string; options: EspeceSelectOption[] }[]).flatMap((g) => g.options)
        : (internalOptions as EspeceSelectOption[])
      : [];

    const filtered = flatOpts.filter((opt) => {
      const labelMatch = opt.label.toLowerCase().includes(filterTerm);
      const descMatch = opt.description?.toLowerCase().includes(filterTerm);
      return labelMatch || descMatch;
    });

    if (groupBy && "options" in (internalOptions[0] ?? {})) {
      const byGroup = filtered.reduce<Record<string, EspeceSelectOption[]>>((acc, opt) => {
        const key = opt.group || "Autres";
        if (!acc[key]) acc[key] = [];
        acc[key].push(opt);
        return acc;
      }, {});
      return Object.entries(byGroup).map(([label, options]) => ({ label, options }));
    }
    return filtered;
  }, [optionsProp, internalOptions, searchMode, inputValue, groupBy]);

  const selectedOption = React.useMemo(() => {
    if (!value) return null;
    const flat = Array.isArray(allOptions) && allOptions.length > 0 && "options" in (allOptions[0] ?? {})
      ? (allOptions as { label: string; options: EspeceSelectOption[] }[]).flatMap((g) => g.options)
      : (allOptions as EspeceSelectOption[]);
    return flat.find((opt) => opt.value === value) ?? null;
  }, [value, allOptions]);

  const isLoading = useInternalFetch ? isLoadingInternal : isLoadingProp ?? false;

  const handleInputChange = React.useCallback((newValue: string, actionMeta: { action: string }) => {
    if (actionMeta.action === "input-change" || actionMeta.action === "input-blur") {
      setInputValue(newValue);
      setSearchTerm(newValue);
    }
  }, []);

  const handleChange = React.useCallback(
    (opt: SingleValue<EspeceSelectOption>) => {
      const val = opt?.value ?? null;
      onChange(val);
      if (onEspeceSelect) {
        onEspeceSelect(opt?.data ?? null);
      }
      setInputValue("");
      setSearchTerm("");
    },
    [onChange, onEspeceSelect]
  );

  const handleBlur = React.useCallback(() => {
    onBlur?.();
  }, [onBlur]);



  return (
    <>
      {/* @ts-expect-error — onAddEspece is a custom prop passed to EspeceMenuList via selectProps */}
      <ReactSelect<EspeceSelectOption, false>
        options={allOptions}
        value={selectedOption}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={isLoading ? "Chargement..." : placeholder}
        isLoading={isLoading}
        noOptionsMessage={() => emptyMessage}
        isSearchable={searchable}
        isClearable={clearable}
        //isDisabled={disabled || isLoading}
        className={className}
        classNamePrefix="react-select"
        blurInputOnSelect={false}
        closeMenuOnSelect
        filterOption={searchMode === "client" ? () => true : undefined}
        formatOptionLabel={(option) => (
          <div className="flex flex-col">
            <span className="font-medium">{option.label}</span>
            {option.description && (
              <span className="text-xs text-muted-foreground">{option.description}</span>
            )}
          </div>
        )}
        // styles={{
        //   control: (base) => ({ ...base, ...controlStyles }),
        //   placeholder: (base) => ({ ...base, color: "hsl(var(--muted-foreground))" }),
        // }}
        menuPortalTarget={menuPortalTarget}
        menuPlacement="auto"
        components={ESPECE_SELECT_COMPONENTS}
        onAddEspece={handleOpenAddDialog}
      />

      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Ajouter une essence</DialogTitle>
          </DialogHeader>
          <AddEspeceQuickForm
            onSuccess={(newEspece) => {
              onChange(String(newEspece.id));
              onEspeceSelect?.(newEspece);
              setIsAddDialogOpen(false);
            }}
            onCancel={() => setIsAddDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
